import React, { useEffect, useState } from 'react';
import Helmet from 'react-helmet';
import env from '@upgrade/ui-utils/env';
import { useTheme } from 'styled-components';

import { Alert, Button } from '@upgrade/react-components';
import { AlertTitle } from '@upgrade/react-components/Alert';
import Header from '../HeaderMenu';
import Footer from '../../Footer';
import { TestSiteAlert, DowntimeAlert } from '../GlobalAlert';
import ClientOnly from '../ClientOnly';
import { MAIN_CONTENT_ID } from '../../../constants/ids-constants';
import useScrollToHash from '../../../utils/useScrollToHash';
import { trackRasEvent } from '../../../utils/ras';
import { LocaleProvider } from '../../../utils/locale';

const TemplateWrapper = props => {
  useScrollToHash();
  const theme = useTheme();
  const [shouldDisplayBanner, setShouldDisplayBanner] = useState(env.GATSBY_BANNER_TEST_SITE_ENABLED === 'true');
  const [shouldDisplayDowntimeAlert, setShouldDisplayDowntimeAlert] = useState(
    Boolean(env.GATSBY_BANNER_DOWNTIME_ALERT_MESSAGE)
  );
  const [shouldDisplayReferralAlert, setshouldDisplayReferralAlert] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);

    if (window) {
      trackRasEvent('landing_page');
      setshouldDisplayReferralAlert(new URLSearchParams(window.location.search).has('referral'));
    }
  }, []);

  const {
    children,
    white,
    smWhite,
    style,
    margin,
    legal,
    customLegal,
    notice,
    signInHref,
    signInText,
    showNavItems,
    showFooter = true,
    useMerchantLoginLink,
    noHeader,
    showNavigation,
    bgColor,
    withNydigNotice,
    fixedHeader,
    whiteFooter,
    showPartnerLogo,
    partnerLogo,
    location,
    isNotFoundPage
  } = props;

  // Page Title is injected from SEO component in all pages.
  return (
    <div data-mounted={String(mounted)} className="layout__wrap" {...{ style }}>
      <Helmet
        title="Upgrade - Affordable Online Personal Loans"
        meta={[
          // Base meta tags, some can be overriden by SeoHelmet in pages that implement it.
          {
            name: 'description',
            content:
              'Check your rate for a low fixed-rate personal loan in just minutes. Borrow up to $50,000 with no prepayment penalties. Checking your rate has no impact to your credit score.'
          },
          // Open graph Top Level Defaults
          { property: 'og:type', content: 'website' },
          { property: 'og:locale', content: 'en_US' },
          { property: 'og:site_name', content: 'Upgrade' },
          // Twitter Defaults
          { name: 'twitter:card', content: 'summary' },
          { name: 'twitter:site', content: '@UpgradeCredit' },
          // FB Defaults
          { property: 'fb:app_id', content: '1745718129090506' },
          {
            name: 'facebook-domain-verification',
            content: env.GATSBY_FACEBOOK_DOMAIN_KEY || process.env.GATSBY_FACEBOOK_DOMAIN_KEY
          },
          {
            name: 'robots',
            content: env.GATSBY_ROBOT_CONTENT_POLICY || process.env.GATSBY_ROBOT_CONTENT_POLICY || 'all'
          }
        ]}
        style={[
          {
            cssText: `
            [data-slate-editor] {
              -webkit-user-modify: read-write !important;
            }
          `
          }
        ]}
      />
      <LocaleProvider location={location}>
        {shouldDisplayBanner && (
          <ClientOnly>
            <TestSiteAlert {...{ onClose: () => setShouldDisplayBanner(false) }} />
          </ClientOnly>
        )}
        {shouldDisplayDowntimeAlert && (
          <ClientOnly>
            <DowntimeAlert {...{ onClose: () => setShouldDisplayDowntimeAlert(false) }} />
          </ClientOnly>
        )}

        {!noHeader && (
          <Header
            {...{
              white,
              smWhite,
              fixedHeader,
              signInHref,
              signInText,
              showNavItems,
              useMerchantLoginLink,
              showNavigation,
              showPartnerLogo,
              partnerLogo,
              location,
              isNotFoundPage
            }}
          />
        )}

        <main className="layout__main" style={{ background: theme.palette[bgColor] || bgColor }}>
          {shouldDisplayReferralAlert && (
            <ClientOnly>
              <div className="container-fluid layout-default section--sm">
                <Alert severity="info">
                  <AlertTitle>
                    The promotion that you clicked on is no longer available. There are no other active promotion
                    programs at this time.
                  </AlertTitle>
                  <Button emphasis="tertiary" onClick={() => setshouldDisplayReferralAlert(false)} size="small">
                    close
                  </Button>
                </Alert>
              </div>
            </ClientOnly>
          )}
          <div id={MAIN_CONTENT_ID}>{children}</div>
        </main>

        {showFooter && <Footer {...{ margin, legal, customLegal, notice, withNydigNotice, whiteFooter }} />}
      </LocaleProvider>
    </div>
  );
};

export default TemplateWrapper;
